import React, { FC } from "react";
import { Box, makeStyles, Typography } from "@material-ui/core";
import { FormattedMessage, useIntl } from "react-intl";
import { Check } from "lucide-react";
import { ConfigGroup, ConfigOfferInterface, getInformation } from "./logic";
import { OffersInfo } from "../../graphql/onboarding/offer/types/OffersInfo";

interface OfferDetailsProps {
  offer: OffersInfo;
  config: ConfigOfferInterface;
}
const useStyles = makeStyles((theme) => ({
  details: {
    borderRadius: theme.shape.borderRadius * 3,
    padding: theme.spacing(2, 2),
    display: "flex",
    flexDirection: "column",

    border: `1px solid rgb(191 219 254 / 1)`,
    margin: theme.spacing(2, 0),
    backgroundColor: "rgb(239 246 255 / 1)",
  },
  title: {
    fontSize: ".925rem",
    fontWeight: 500,
  },
  option: {
    display: "flex",
    justifyContent: "space-between",
    "& p": {
      fontSize: ".875rem",
      lineHeight: "1.25rem",
      color: "#4e5d78",
    },
    flexDirection: "row",
  },
  message: {
    display: "flex",
    justifyContent: "center",
    flexDirection: "row",
    gap: ".5rem",
  },
  name: {
    fontWeight: 700,
    fontSize: "1.525rem",
    lineHeight: "1.75rem",
    textTransform: "uppercase",
  },
  price: {
    fontSize: "1.425rem",
    lineHeight: "1.75rem",
    color: "#4e5d78",
  },
  header: {
    display: "flex",
    justifyContent: "center",
    flexDirection: "column",
    alignItems: "center",
  },
}));

const OfferDetails: FC<OfferDetailsProps> = ({ offer: _offer, config }) => {
  const classes = useStyles();
  const intl = useIntl();
  const offer = getInformation(_offer);
  const { businessCard, directDebit, payout, transactions, users } = config;
  const {
    ddMonthlyLimit,
    ddTransactionLimit,
    limitUser,
    payouts,
    payoutsInst,
    physicalCard,
    price,
    transactionDailyLimit,
    transactionLimit,
    transactionMonthlyLimit,
    virtualCard,
  } = offer;

  const OptionDisplay = ({
    message,
    value,
  }: {
    message: string;
    value: number | string | React.ReactNode;
  }) => (
    <Box className={classes.option}>
      <Box className={classes.message}>
        <Check style={{ width: 14, color: "rgb(34 197 94 / 1)" }} />
        <Typography>{message}</Typography>
      </Box>
      <Typography align="right">{value}</Typography>
    </Box>
  );

  const ConfigGroupDisplay = ({
    group,
    offerValues,
  }: {
    group: ConfigGroup;
    offerValues: Record<string, any>;
  }) => {
    if (!group?.display) return null;

    return (
      <>
        <Typography className={classes.title} variant="subtitle1">
          • {group.title}
        </Typography>
        <Box>
          {Object.entries(group.optionlist).map(([key, option]) => {
            if (option.display) {
              return (
                <OptionDisplay
                  key={key}
                  message={option.title}
                  value={offerValues[key]}
                />
              );
            }
            return null;
          })}
        </Box>
      </>
    );
  };

  return (
    <Box className={classes.details}>
      <Box className={classes.header}>
        <Typography className={classes.name}>{_offer?.name}</Typography>
        <Typography className={classes.price}>
          {price == "0" ? intl.formatMessage({ id: "offer.free" }) : price}
          {price == "0" ? "" : " €/mois"}
        </Typography>
      </Box>

      <ConfigGroupDisplay
        group={businessCard}
        offerValues={{
          physical: physicalCard,
          virtual: virtualCard,
          cardTransaction: <FormattedMessage id="offer.noLimit" />,
        }}
      />

      <ConfigGroupDisplay
        group={payout}
        offerValues={{
          normal: payouts,
          inst: payoutsInst,
        }}
      />

      <ConfigGroupDisplay
        group={transactions}
        offerValues={{
          autorizedTransaction: transactionLimit,
          autorizedTransactionDaily: transactionDailyLimit,
          autorizedTransactionMonthly: transactionMonthlyLimit,
        }}
      />

      <ConfigGroupDisplay
        group={directDebit}
        offerValues={{
          autorizedDirectDebit: ddTransactionLimit,
          autorizedDirectDebitMonthly: ddMonthlyLimit,
        }}
      />

      <ConfigGroupDisplay
        group={users}
        offerValues={{
          users: limitUser,
        }}
      />
    </Box>
  );
};

export default OfferDetails;
